import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useConfig } from '../../contexts/ConfigContext';
import { useTranslation } from 'react-i18next';
import { useResponsiveCTAs } from '../../hooks/useResponsiveCTAs';
import { trackEvent } from '../../utils/analytics';
import styles from './BreathingCTA.module.css';

interface BreathingCTAProps {
  side: 'left' | 'right';
  onAction: () => void;
  particleSystem?: any;
  priority?: 'primary' | 'secondary';
  className?: string;
}

export const BreathingCTA: React.FC<BreathingCTAProps> = ({
  side,
  onAction,
  particleSystem,
  priority = 'primary',
  className = ''
}) => {
  const { config } = useConfig();
  const { t } = useTranslation();
  const { layout, orientation, isTouch } = useResponsiveCTAs();
  const ctaRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();

  const [isHovered, setIsHovered] = useState(false);
  const [breathPhase, setBreathPhase] = useState<'inhale' | 'exhale'>('inhale');
  const [isVisible, setIsVisible] = useState(false);

  const ctaConfig = config.siteSettings.dualCTASettings[side];
  const animationConfig = config.siteSettings.dualCTASettings.animation;

  // Debug logging
  console.log('BreathingCTA render:', {
    side,
    enabled: config.siteSettings.dualCTASettings.enabled,
    ctaConfig,
    layout
  });

  // Intersection Observer for lazy animation initialization
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (ctaRef.current) {
      observer.observe(ctaRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Breathing animation cycle
  useEffect(() => {
    if (!animationConfig.breathingEnabled || shouldReduceMotion || !isVisible) return;

    const interval = setInterval(() => {
      setBreathPhase(prev => prev === 'inhale' ? 'exhale' : 'inhale');
    }, animationConfig.breathingInterval / 2);

    return () => clearInterval(interval);
  }, [animationConfig, shouldReduceMotion, isVisible]);

  // Particle attraction effect
  useEffect(() => {
    if (!ctaConfig.particleAttraction || !particleSystem || !ctaRef.current || layout === 'mobile') return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!ctaRef.current) return;

      const rect = ctaRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const distance = Math.sqrt(
        Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2)
      );

      const attractionRadius = layout === 'desktop' ? 250 : 150;

      if (distance < attractionRadius) {
        window.dispatchEvent(new CustomEvent('ctaAttraction', {
          detail: {
            x: centerX,
            y: centerY,
            strength: 1 - (distance / attractionRadius),
            side
          }
        }));
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [ctaConfig.particleAttraction, particleSystem, side, layout]);

  const handleClick = () => {
    trackEvent('cta_click', {
      side,
      type: ctaConfig.type,
      priority,
      layout,
      orientation,
      breath_phase: breathPhase
    });
    onAction();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // Animation variants
  const breathingVariants = {
    inhale: {
      scale: shouldReduceMotion ? 1 : 1,
      boxShadow: shouldReduceMotion
        ? '0 0 20px rgba(255, 255, 255, 0.1)'
        : '0 0 30px rgba(255, 255, 255, 0.1)',
      transition: {
        duration: shouldReduceMotion ? 0 : animationConfig.breathingInterval / 2000,
        ease: "easeInOut" as const
      }
    },
    exhale: {
      scale: shouldReduceMotion ? 1 : 0.98,
      boxShadow: shouldReduceMotion
        ? '0 0 20px rgba(255, 255, 255, 0.1)'
        : '0 0 50px rgba(255, 255, 255, 0.2)',
      transition: {
        duration: shouldReduceMotion ? 0 : animationConfig.breathingInterval / 2000,
        ease: "easeInOut" as const
      }
    }
  };

  const glowVariants = {
    idle: {
      opacity: config.siteSettings.dualCTASettings.harmony.glowIntensity * 0.7
    },
    hover: {
      opacity: config.siteSettings.dualCTASettings.harmony.glowIntensity,
      transition: { duration: 0.3 }
    }
  };

  const containerVariants = {
    hidden: {
      opacity: 0,
      y: layout === 'mobile' ? 20 : 0,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };

  // Dynamic positioning for desktop
  const dynamicStyle = layout !== 'mobile' ? {
    left: side === 'left' ? ctaConfig.position.desktop.x : 'auto',
    right: side === 'right' ? ctaConfig.position.desktop.x : 'auto',
    top: ctaConfig.position.desktop.y,
    transform: side === 'left' ? 'translate(-50%, -50%)' : 'translate(50%, -50%)'
  } : {};

  return (
    <motion.div
      ref={ctaRef}
      className={`${styles.breathingCTA} ${styles[side]} ${className}`}
      style={dynamicStyle}
      variants={containerVariants}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      onMouseEnter={() => !isTouch && setIsHovered(true)}
      onMouseLeave={() => !isTouch && setIsHovered(false)}
      role="button"
      tabIndex={0}
      aria-label={String(t(`dualCTAs.${side}.ariaLabel`))}
      onKeyPress={handleKeyPress}
    >
      {/* Organic glow backdrop */}
      <motion.div
        className={styles.glowBackdrop}
        variants={glowVariants}
        animate={isHovered ? "hover" : "idle"}
      />

      {/* Main CTA container */}
      <motion.div
        className={styles.ctaContainer}
        variants={breathingVariants}
        animate={isVisible ? breathPhase : 'inhale'}
        whileHover={!shouldReduceMotion ? {
          scale: 1.02,
          transition: { duration: 0.2 }
        } : {}}
        whileTap={!shouldReduceMotion ? {
          scale: 0.98,
          transition: { duration: 0.1 }
        } : {}}
      >
        <div className={styles.ctaContent}>
          <h3 className={styles.title}>
            {t(`dualCTAs.${side}.title`)}
          </h3>
          <p className={styles.subtitle}>
            {t(`dualCTAs.${side}.subtitle`)}
          </p>
        </div>

        <motion.button
          className={styles.ctaButton}
          onClick={handleClick}
          whileHover={!shouldReduceMotion ? {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          } : {}}
          disabled={false}
        >
          <span className={styles.buttonText}>
            {t(`dualCTAs.${side}.buttonText`)}
          </span>
          <motion.span
            className={styles.buttonIcon}
            animate={{ x: isHovered && !isTouch ? 5 : 0 }}
            transition={{ duration: 0.2 }}
          >
            →
          </motion.span>
        </motion.button>
      </motion.div>

      {/* Particle attraction visualizer */}
      {ctaConfig.particleAttraction && layout !== 'mobile' && (
        <svg className={styles.attractionField} width="400" height="400">
          <defs>
            <radialGradient id={`attraction-${side}`}>
              <stop offset="0%" stopColor="white" stopOpacity="0.1" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle
            cx="200"
            cy="200"
            r={isHovered ? 180 : 150}
            fill={`url(#attraction-${side})`}
            style={{
              transition: shouldReduceMotion ? 'none' : 'r 0.3s ease-out'
            }}
          />
        </svg>
      )}
    </motion.div>
  );
};

export default BreathingCTA;